import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useReveal } from "@/lib/useReveal";
import { BACKEND_URL } from "@/lib/backendUrl";
import { toast } from "sonner";

const API = `${BACKEND_URL}/api`;
const MAX_MB = 10;
const ACCEPT = ".pdf,.doc,.docx,.txt,.md,.rtf,.odt,.zip";

export const Contacts = () => {
    const ref = useReveal();
    const fileRef = useRef(null);
    const [form, setForm] = useState({ name: "", email: "", message: "", package: "" });
    const [file, setFile] = useState(null);
    const [fileErr, setFileErr] = useState("");
    const [consent, setConsent] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | loading | success | error
    const [errorMsg, setErrorMsg] = useState("");

    const onChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        setFileErr("");
        if (!f) {
            setFile(null);
            return;
        }
        if (f.size > MAX_MB * 1024 * 1024) {
            setFileErr(`Файл больше ${MAX_MB} МБ. Уменьшите размер или отправьте в Telegram.`);
            setFile(null);
            if (fileRef.current) fileRef.current.value = "";
            return;
        }
        setFile(f);
    };

    const clearFile = () => {
        setFile(null);
        setFileErr("");
        if (fileRef.current) fileRef.current.value = "";
    };

    const formatBytes = (b) => {
        if (b < 1024) return `${b} Б`;
        if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} КБ`;
        return `${(b / (1024 * 1024)).toFixed(2)} МБ`;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
            setErrorMsg("Заполните все обязательные поля.");
            setStatus("error");
            return;
        }
        if (!consent) {
            setErrorMsg("Подтвердите согласие на обработку персональных данных.");
            setStatus("error");
            return;
        }
        setStatus("loading");
        setErrorMsg("");
        try {
            const fd = new FormData();
            fd.append("name", form.name.trim());
            fd.append("email", form.email.trim());
            fd.append("message", form.message.trim());
            if (form.package) fd.append("package", form.package);
            if (file) fd.append("file", file);

            await axios.post(`${API}/contact`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setStatus("success");
            setForm({ name: "", email: "", message: "", package: "" });
            clearFile();
            setConsent(false);
            toast.success("Спасибо! Я свяжусь с вами в ближайшее время.");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg =
                typeof detail === "string"
                    ? detail
                    : "Не удалось отправить. Попробуйте Telegram @ekulakova1.";
            setErrorMsg(msg);
            setStatus("error");
            toast.error(msg);
        }
    };

    return (
        <section
            id="contact"
            data-testid="contacts-section"
            className="relative py-28 sm:py-40 border-t border-[#1B2A41]/15 bg-[#F4F1EA]"
        >
            <div ref={ref} className="vnutri-reveal max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-20">
                    <div className="lg:col-span-5">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-12 h-px bg-[#C46342]" />
                            <span className="text-[11px] tracking-[0.4em] uppercase font-semibold text-[#C46342]">
                                05 · Контакты
                            </span>
                        </div>
                        <h2
                            data-testid="contacts-title"
                            className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-[-0.03em] text-[#1B2A41] font-extralight leading-[1.02]"
                        >
                            Связаться <span className="font-semibold">со мной</span>
                        </h2>
                        <p className="mt-8 max-w-md text-base leading-relaxed text-[#1B2A41]/75">
                            Я отвечаю на сообщения лично. Расскажите о ситуации,
                            приложите детальный документ, если есть, — я предложу
                            формат и время для разговора.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                data-testid="contact-telegram"
                                href="https://t.me/ekulakova1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group block border border-[#1B2A41]/20 bg-white p-6 hover:border-[#C46342] transition-colors"
                            >
                                <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#C46342]">
                                    Telegram · основной канал
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-3">
                                    <span className="text-2xl font-semibold tracking-tight text-[#1B2A41]">
                                        @ekulakova1
                                    </span>
                                    <span className="text-[#C46342] group-hover:translate-x-1 transition-transform">
                                        ↗
                                    </span>
                                </div>
                                <div className="mt-3 text-sm text-[#1B2A41]/65 leading-relaxed">
                                    Самый быстрый способ. Личные сообщения — я
                                    отвечу сама.
                                </div>
                            </a>

                            <a
                                data-testid="contact-email"
                                href="mailto:elena@vnutri.team"
                                className="group block border border-[#1B2A41]/20 bg-white p-6 hover:border-[#C46342] transition-colors"
                            >
                                <div className="text-[10px] tracking-[0.32em] uppercase font-semibold text-[#C46342]">
                                    Email
                                </div>
                                <div className="mt-3 flex items-center justify-between gap-3">
                                    <span className="text-2xl font-semibold tracking-tight text-[#1B2A41]">
                                        elena@vnutri.team
                                    </span>
                                    <span className="text-[#C46342] group-hover:translate-x-1 transition-transform">
                                        ↗
                                    </span>
                                </div>
                                <div className="mt-3 text-sm text-[#1B2A41]/65 leading-relaxed">
                                    Для подробных писем и вложений.
                                </div>
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            encType="multipart/form-data"
                            className="border border-[#1B2A41]/20 bg-white p-8 lg:p-10"
                        >
                            <div className="text-[11px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/60 mb-6">
                                Оставить заявку
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label
                                        htmlFor="name"
                                        className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                    >
                                        Имя *
                                    </label>
                                    <input
                                        id="name"
                                        name="name"
                                        type="text"
                                        value={form.name}
                                        onChange={onChange}
                                        data-testid="contact-input-name"
                                        required
                                        className="w-full bg-transparent border-0 border-b border-[#1B2A41]/25 focus:border-[#C46342] outline-none py-3 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors"
                                        placeholder="Как к вам обращаться"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="email"
                                        className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                    >
                                        Email *
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={form.email}
                                        onChange={onChange}
                                        data-testid="contact-input-email"
                                        required
                                        className="w-full bg-transparent border-0 border-b border-[#1B2A41]/25 focus:border-[#C46342] outline-none py-3 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors"
                                        placeholder="you@company.com"
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="package"
                                        className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                    >
                                        Интересующий формат
                                    </label>
                                    <select
                                        id="package"
                                        name="package"
                                        value={form.package}
                                        onChange={onChange}
                                        data-testid="contact-select-package"
                                        className="w-full bg-transparent border-0 border-b border-[#1B2A41]/25 focus:border-[#C46342] outline-none py-3 text-base text-[#1B2A41] transition-colors appearance-none cursor-pointer"
                                    >
                                        <option value="">Не выбран — помогите подобрать</option>
                                        <option value="Экспертная поддержка">
                                            Экспертная поддержка
                                        </option>
                                        <option value="Вернуть контроль">
                                            Вернуть контроль
                                        </option>
                                        <option value="Запуск отдела разработки">
                                            Запуск отдела разработки
                                        </option>
                                    </select>
                                </div>

                                <div>
                                    <label
                                        htmlFor="message"
                                        className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                    >
                                        Сообщение *
                                    </label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows="4"
                                        value={form.message}
                                        onChange={onChange}
                                        data-testid="contact-input-message"
                                        required
                                        className="w-full bg-transparent border border-[#1B2A41]/20 focus:border-[#C46342] outline-none py-3 px-4 text-base text-[#1B2A41] placeholder:text-[#1B2A41]/30 transition-colors resize-none"
                                        placeholder="Коротко о ситуации: размер команды, продукт, что сейчас болит."
                                    />
                                </div>

                                {/* File upload */}
                                <div>
                                    <label
                                        htmlFor="file"
                                        className="block text-[10px] tracking-[0.32em] uppercase font-semibold text-[#1B2A41]/70 mb-2"
                                    >
                                        ТЗ или файл · по желанию
                                    </label>
                                    <input
                                        ref={fileRef}
                                        id="file"
                                        name="file"
                                        type="file"
                                        accept={ACCEPT}
                                        onChange={onFileChange}
                                        data-testid="contact-input-file"
                                        className="hidden"
                                    />
                                    {!file ? (
                                        <label
                                            htmlFor="file"
                                            className="block cursor-pointer border border-dashed border-[#1B2A41]/35 p-5 text-center hover:border-[#C46342] hover:bg-[#F4F1EA] transition-colors"
                                            data-testid="contact-file-dropzone"
                                        >
                                            <div className="flex items-center justify-center gap-3 text-sm text-[#1B2A41]/75">
                                                <span className="inline-flex w-8 h-8 items-center justify-center bg-[#1B2A41] text-[#F4F1EA]">
                                                    +
                                                </span>
                                                <span>
                                                    <span className="font-semibold text-[#1B2A41]">
                                                        Прикрепить файл
                                                    </span>{" "}
                                                    · до {MAX_MB} МБ
                                                </span>
                                            </div>
                                            <div className="mt-2 text-[11px] tracking-[0.18em] uppercase text-[#1B2A41]/50">
                                                PDF · DOC · DOCX · TXT · MD · RTF · ODT · ZIP
                                            </div>
                                        </label>
                                    ) : (
                                        <div
                                            data-testid="contact-file-preview"
                                            className="flex items-center justify-between gap-4 border border-[#1B2A41]/20 bg-[#F4F1EA] p-4"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <span className="inline-flex w-8 h-8 shrink-0 items-center justify-center bg-[#C46342] text-[#F4F1EA] text-sm font-semibold">
                                                    ✓
                                                </span>
                                                <div className="min-w-0">
                                                    <div className="text-sm font-semibold text-[#1B2A41] truncate">
                                                        {file.name}
                                                    </div>
                                                    <div className="text-[11px] tracking-[0.18em] uppercase text-[#1B2A41]/60">
                                                        {formatBytes(file.size)}
                                                    </div>
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={clearFile}
                                                data-testid="contact-file-clear"
                                                className="shrink-0 text-[11px] tracking-[0.18em] uppercase font-semibold text-[#1B2A41]/60 hover:text-[#C46342] transition-colors"
                                            >
                                                Убрать
                                            </button>
                                        </div>
                                    )}
                                    {fileErr && (
                                        <div
                                            data-testid="contact-file-error"
                                            className="mt-2 text-xs text-[#C46342]"
                                        >
                                            {fileErr}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {status === "error" && errorMsg && (
                                <div
                                    data-testid="contact-error"
                                    className="mt-6 px-4 py-3 border border-[#C46342] bg-[#F4F1EA] text-sm text-[#1B2A41]"
                                >
                                    {errorMsg}
                                </div>
                            )}
                            {status === "success" && (
                                <div
                                    data-testid="contact-success"
                                    className="mt-6 px-4 py-3 border border-[#1B2A41]/25 bg-[#F4F1EA] text-sm text-[#1B2A41]"
                                >
                                    Заявка отправлена. Я отвечу лично в
                                    ближайшее рабочее время.
                                </div>
                            )}

                            <div className="mt-8 border-t border-[#1B2A41]/10 pt-6">
                                <label
                                    htmlFor="consent"
                                    data-testid="contact-consent-label"
                                    className="flex items-start gap-3 cursor-pointer group select-none"
                                >
                                    <input
                                        id="consent"
                                        name="consent"
                                        type="checkbox"
                                        checked={consent}
                                        onChange={(e) => setConsent(e.target.checked)}
                                        data-testid="contact-consent-checkbox"
                                        required
                                        className="mt-[3px] shrink-0 h-4 w-4 appearance-none border border-[#1B2A41]/40 bg-white checked:bg-[#C46342] checked:border-[#C46342] focus:outline-none focus:ring-2 focus:ring-[#C46342]/30 cursor-pointer transition-colors"
                                        style={{
                                            backgroundImage: consent
                                                ? "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23F4F1EA' stroke-width='2' stroke-linecap='square' d='M3 8l3.5 3.5L13 5'/%3E%3C/svg%3E\")"
                                                : "none",
                                            backgroundSize: "100% 100%",
                                            backgroundRepeat: "no-repeat",
                                        }}
                                    />
                                    <span className="text-sm leading-relaxed text-[#1B2A41]/85">
                                        Отправляя заявку, вы соглашаетесь на
                                        обработку персональных данных в
                                        соответствии с{" "}
                                        <Link
                                            to="/privacy"
                                            data-testid="contact-consent-privacy-link"
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-[#C46342] font-medium underline underline-offset-4 decoration-[#C46342]/40 hover:decoration-[#C46342] transition-colors"
                                        >
                                            Политикой&nbsp;конфиденциальности
                                        </Link>
                                        .
                                    </span>
                                </label>
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    type="submit"
                                    data-testid="contact-submit"
                                    disabled={status === "loading"}
                                    className="inline-flex items-center justify-center gap-3 bg-[#C46342] hover:bg-[#8f4631] disabled:opacity-60 disabled:cursor-not-allowed text-[#F4F1EA] px-8 py-4 text-xs font-semibold tracking-[0.18em] uppercase transition-colors"
                                >
                                    {status === "loading" ? "Отправляю…" : "Отправить заявку"}
                                    {status !== "loading" && (
                                        <svg
                                            width="16"
                                            height="12"
                                            viewBox="0 0 18 12"
                                            fill="none"
                                        >
                                            <path
                                                d="M1 6H17M17 6L12 1M17 6L12 11"
                                                stroke="currentColor"
                                                strokeWidth="1.5"
                                            />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
